import { useCallback, useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../auth/index.ts";
import { Drawer } from "../../auth/Drawer.tsx";
import { Modal } from "../../auth/Modal.tsx";
import { StatusPill, type PillTone } from "../../auth/Pills.tsx";
import type { OperatorRole } from "@oathera/contract/rbac";
import { ApiError } from "../../lib/api.ts";
import { INVALID_TIME_PLACEHOLDER, isoAttr, toValidDate } from "../../lib/datetime.ts";
import { OtpInput } from "../../components/OtpInput.tsx";
import {
  generateLicense,
  getLicenseStatus,
  isStepUpRequired,
  licenseQueryKey,
  newOpId,
  regenerateLicense,
  revealLicense,
  revokeLicense,
  type LicenseScope,
  type LicenseView,
} from "../../lib/license-client.ts";
import "./account.css";
import "./license-card.css";

/**
 * The shared LICENSE CARD (license-key-cards task 14.2; requirements 6.8, 7.2, 7.5, 11.5, 11.6, 11.10,
 * 12.x, 15.x, 16.x, 17.x). One component renders both keys as a credit-card style card:
 *
 *   - `scope="tenant"` -> the team's PRODUCTION key (Members page, admin only)
 *   - `scope="user"`   -> the member's own DEVELOPMENT key (Account page)
 *
 * PROPS. `scope` is required. `heading` replaces the visible title (the accessible name still carries
 * the scope). `className` lets a placement add a layout class. There is deliberately NO `teamId`
 * prop: the server resolves the tenant from the session, so a prop that named a different team
 * would make the card show one team while the server acts on another. The card reads the Selected_Team
 * and role from `useAuth()` and re-mounts when either the scope or the team changes.
 *
 * KEY HANDLING (Requirement 11.5, 11.6). Only the metadata status read goes through TanStack Query.
 * A revealed License_Key lives in ONE `useState` value of the inner component. The inner component is
 * keyed by `scope:team`, so a team switch unmounts it and the key (and any open dialog) disappear with
 * it; so does leaving the page. It is also cleared on Hide, after a revoke or regenerate, and when the
 * license it belongs to is no longer the face license. Nothing is written to storage, a URL or the
 * query cache. While masked, the DOM holds only the server-provided mask (prefix + bullets + last 4).
 *
 * The key-bearing calls are plain async functions called from event handlers; in-flight calls are
 * aborted on unmount and their late results are dropped.
 *
 * No `console` call anywhere in this file (Requirement 11.1 spirit).
 */

export interface LicenseCardProps {
  /** `tenant` = production Tenant_Key (admin), `user` = development User_Key. */
  readonly scope: LicenseScope;
  /** Optional visible title; defaults to "Production license key" / "Development license key". */
  readonly heading?: string;
  /** Optional extra class for the placement (the card itself sizes to 280-480 px). */
  readonly className?: string;
  /**
   * Hide the visible title. The title stays in the DOM as screen-reader-only text, so the card keeps
   * its accessible name. Used where the card's own environment badge already says what it is.
   */
  readonly hideTitle?: boolean;
}

// ---------------------------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------------------------

const ENV_WORD: Readonly<Record<LicenseScope, "production" | "development">> = {
  tenant: "production",
  user: "development",
};

/** The sentence the confirmations and the guidance share (Requirement 7.5). */
const OLD_KEY_WARNING = "Devices still using the old key will be affected.";

/**
 * What the key field shows while the key is hidden. It is a fixed string, not derived from the key,
 * so the hidden field holds nothing of the real key (not even its last characters) and the field
 * looks the same whether or not the key has ever been revealed.
 */
const HIDDEN_KEY_TEXT = `OATH_${"*".repeat(14)}`;

const NOT_CONFIGURED_TEXT = "License issuing is not configured for this environment";
const COPY_FAILED_TEXT = "Copy failed. Use Reveal and copy the key manually.";

/** Operation ids stay stable across retries of the same action (Requirement 7.9). */
type Action =
  | { readonly kind: "generate" }
  | { readonly kind: "regenerate"; readonly licenseId: string }
  | { readonly kind: "revoke"; readonly licenseId: string }
  | { readonly kind: "reveal"; readonly licenseId: string }
  | { readonly kind: "copy"; readonly licenseId: string };

type Dialog =
  | { readonly kind: "confirm"; readonly intent: "regenerate" | "revoke" }
  | { readonly kind: "stepup"; readonly action: Action; readonly wrongCode: boolean };

/** The holder of a revealed key: which license it belongs to travels with it. */
interface Revealed {
  readonly licenseId: string;
  readonly key: string;
}

class ClipboardUnavailableError extends Error {}

/** Short form of a key id: `591a…10db`. */
function shortKid(kid: string): string {
  return kid.length > 9 ? `${kid.slice(0, 4)}…${kid.slice(-4)}` : kid;
}

function formatDate(ms: number): string {
  const date = toValidDate(ms);
  return date === null ? INVALID_TIME_PLACEHOLDER : date.toLocaleDateString();
}

function DateText(props: { readonly ms: number }): ReactNode {
  const iso = isoAttr(props.ms);
  return <time dateTime={iso}>{formatDate(props.ms)}</time>;
}

const STATUS_LABEL = { active: "Active", expired: "Expired", revoked: "Revoked" } as const;
const STATUS_TONE: Readonly<Record<LicenseView["status"], PillTone>> = {
  active: "safe",
  expired: "warn",
  revoked: "threat",
};

/** "42 seconds" / "3 minutes"; null when the server gave no hint. */
function waitPhrase(seconds: number | null): string {
  if (seconds === null) return "a little later";
  if (seconds < 60) return `${Math.max(1, Math.ceil(seconds))} seconds`;
  const minutes = Math.ceil(seconds / 60);
  return minutes === 1 ? "1 minute" : `${minutes} minutes`;
}

/** Friendly wording for a failed read or write; the same cases the host screens word (17.2). */
function licenseErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError) {
    switch (error.code) {
      case "rate_limited":
        return `Too many license changes. Try again in ${waitPhrase(error.retryAfterSeconds)}.`;
      case "not_configured":
        return `${NOT_CONFIGURED_TEXT}.`;
      case "no_key_holder":
        return "This team has no active administrator to hold the key. Restore an admin, then try again.";
      case "already_active":
        return "A license key is already active here. The card has been refreshed.";
      case "not_active":
        return "That license key is no longer active. The card has been refreshed.";
      default:
        break;
    }
    if (error.kind === "waking") return "The control plane is waking up. Please try again in a moment.";
    if (error.kind === "network") return "We could not reach the control plane. Check your connection.";
    if (error.kind === "forbidden") return "You are not permitted to do that.";
    if (error.kind === "malformed") return "That action is not available yet. Please try again later.";
  }
  return fallback;
}

/** Codes that mean the next attempt is a NEW action, so its op id must not be replayed. */
const DEFINITIVE_CODES: ReadonlySet<string> = new Set([
  "forbidden",
  "no_key_holder",
  "already_active",
  "not_active",
  "not_configured",
]);

// ---------------------------------------------------------------------------------------------
// Icons (decorative; the badge text carries the meaning)
// ---------------------------------------------------------------------------------------------

function ShieldIcon(): ReactNode {
  return (
    <svg viewBox="0 0 24 24" width="12" height="12" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3l8 3v6c0 4.5-3.2 7.9-8 9-4.8-1.1-8-4.5-8-9V6l8-3z" />
      <path d="M8.5 12l2.5 2.5 4.5-5" />
    </svg>
  );
}

function WrenchIcon(): ReactNode {
  return (
    <svg viewBox="0 0 24 24" width="12" height="12" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
    </svg>
  );
}

/** The eye (Reveal) and eye-off (Hide) icons of the key toggle. Decorative: the button has a name. */
function EyeIcon(props: { readonly off: boolean }): ReactNode {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z" />
      <circle cx="12" cy="12" r="3" />
      {props.off && <path d="M4 4l16 16" />}
    </svg>
  );
}

// ---------------------------------------------------------------------------------------------
// The step-up code prompt (same pattern as the team-create form)
// ---------------------------------------------------------------------------------------------

function StepUpDialog(props: {
  readonly wrongCode: boolean;
  readonly busy: boolean;
  readonly onSubmit: (code: string) => void;
  readonly onCancel: () => void;
}): ReactNode {
  const inputId = useId();
  const formId = useId();
  const [code, setCode] = useState("");
  const complete = /^\d{6}$/.test(code);

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    if (complete && !props.busy) props.onSubmit(code);
  };

  return (
    <Modal
      open
      onClose={props.onCancel}
      title="Confirm with your authenticator"
      description="This is a protected action. Enter the current 6-digit code from your authenticator app to continue."
      footer={
        <>
          <button type="button" className="oa-account__btn oa-account__btn--ghost" disabled={props.busy} onClick={props.onCancel}>
            Cancel
          </button>
          <button type="submit" form={formId} className="oa-account__btn" disabled={!complete || props.busy}>
            {props.busy ? "Verifying…" : "Verify and continue"}
          </button>
        </>
      }
    >
      <form id={formId} className="oa-account__form" onSubmit={submit}>
        <div className="oa-account__row">
          <label className="oa-account__label" htmlFor={inputId}>
            Authentication code
          </label>
          <OtpInput id={inputId} value={code} onChange={setCode} invalid={props.wrongCode} disabled={props.busy} align="start" />
          {props.wrongCode && (
            <span className="oa-account__field-error" role="alert">
              That code was not accepted. Enter a fresh 6-digit code.
            </span>
          )}
        </div>
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------------------------
// The card
// ---------------------------------------------------------------------------------------------

export function LicenseCard(props: LicenseCardProps): ReactNode {
  const auth = useAuth();
  const teamId = auth.identity?.team?.id ?? null;
  const role = auth.identity?.role ?? null;

  if (teamId === null) {
    return (
      <article className={joinClass("oa-license", props.className)} data-scope={props.scope} data-state="no-team" aria-label={`${titleCase(ENV_WORD[props.scope])} license key`}>
        <p className="oa-account__state" role="status">
          Select a team to see its license key.
        </p>
      </article>
    );
  }

  // Keyed by scope and team: a switch unmounts the body, which drops the revealed key, any open
  // dialog and any in-flight call with it (Requirement 11.5).
  return <LicenseCardBody key={`${props.scope}:${teamId}`} {...props} teamId={teamId} role={role} />;
}

function titleCase(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1);
}

function joinClass(...parts: ReadonlyArray<string | undefined>): string {
  return parts.filter((part): part is string => part !== undefined && part !== "").join(" ");
}

function LicenseCardBody(props: LicenseCardProps & { readonly teamId: string; readonly role: OperatorRole | null }): ReactNode {
  const { scope, teamId } = props;
  const env = ENV_WORD[scope];
  const envLabel = env.toUpperCase();
  const queryClient = useQueryClient();

  const headingId = useId();
  const keyRegionId = useId();
  const reasonId = useId();

  const headingRef = useRef<HTMLHeadingElement | null>(null);
  const revealButtonRef = useRef<HTMLButtonElement | null>(null);
  const regenerateButtonRef = useRef<HTMLButtonElement | null>(null);
  const controllerRef = useRef<AbortController | null>(null);
  // Stable op ids per action so a retry of the same action is recognised by the server.
  const opIdsRef = useRef<Map<string, string>>(new Map());

  const [revealed, setRevealed] = useState<Revealed | null>(null);
  const [busy, setBusy] = useState<Action["kind"] | null>(null);
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [limitedUntil, setLimitedUntil] = useState<number | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  // Where focus goes once the card is idle again: buttons are disabled while a call is in flight,
  // and a disabled button cannot take focus, so the move waits for `busy` to clear.
  const [pendingFocus, setPendingFocus] = useState<"reveal" | "regenerate" | "heading" | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    controllerRef.current = controller;
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (pendingFocus === null || busy !== null) return;
    const target = pendingFocus === "reveal" ? revealButtonRef : pendingFocus === "regenerate" ? regenerateButtonRef : headingRef;
    (target.current ?? headingRef.current)?.focus();
    setPendingFocus(null);
  }, [pendingFocus, busy]);

  // Rate-limit window: write buttons stay disabled until the server's Retry-After has passed.
  useEffect(() => {
    if (limitedUntil === null) return;
    const timer = setTimeout(() => setLimitedUntil(null), Math.max(0, limitedUntil - Date.now()));
    return () => clearTimeout(timer);
  }, [limitedUntil]);

  const statusQuery = useQuery({
    queryKey: licenseQueryKey(scope, teamId),
    queryFn: ({ signal }) => getLicenseStatus(scope, signal),
    staleTime: 15_000,
    retry: 1,
    retryDelay: 800,
  });

  const data = statusQuery.data;
  const current = data?.license ?? null; // the stored-active row (active or expired)
  const face: LicenseView | null = current ?? data?.history[0] ?? null;
  const faceIsCurrent = face !== null && face === current;
  // `face` is the stored-active row when there is one, else the newest history row (shown stamped).
  const roleAllowsManage = scope === "tenant" ? props.role === "admin" : props.role === "operator" || props.role === "admin";
  const canManage = data !== undefined && data.can_manage && roleAllowsManage;
  const configured = data?.issuing.configured ?? true;
  const isActive = faceIsCurrent && face.status === "active";
  const isExpired = faceIsCurrent && face.status === "expired";
  const isHistorical = face !== null && !faceIsCurrent;

  // The revealed key is shown only for the live, active license it was fetched for.
  const shownKey = revealed !== null && isActive && face.license_id === revealed.licenseId ? revealed.key : null;
  const writeBlocked = limitedUntil !== null;
  const working = busy !== null;

  // Drop the key from memory as soon as it can no longer be shown (revoked, expired, replaced).
  useEffect(() => {
    if (revealed !== null && shownKey === null && data !== undefined) setRevealed(null);
  }, [revealed, shownKey, data]);

  let state: string;
  if (statusQuery.isPending) state = "loading";
  else if (statusQuery.isError && data === undefined) state = "error";
  else if (face === null) state = configured ? "empty" : "not-configured";
  else if (isActive) state = "active";
  else if (isExpired) state = "expired";
  else state = "revoked";

  const refresh = useCallback(async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: licenseQueryKey(scope, teamId) });
  }, [queryClient, scope, teamId]);

  const opIdFor = useCallback((action: Action): string => {
    const id = `${action.kind}:${"licenseId" in action ? action.licenseId : ""}`;
    const existing = opIdsRef.current.get(id);
    if (existing !== undefined) return existing;
    const fresh = newOpId();
    opIdsRef.current.set(id, fresh);
    return fresh;
  }, []);

  const forgetOpId = useCallback((action: Action): void => {
    opIdsRef.current.delete(`${action.kind}:${"licenseId" in action ? action.licenseId : ""}`);
  }, []);

  /** Run one user action. Everything key-bearing happens here, in an event-handler call chain. */
  const perform = useCallback(
    async (action: Action, mfaCode?: string): Promise<void> => {
      const signal = controllerRef.current?.signal;
      if (signal === undefined || signal.aborted) return;
      const mfa = mfaCode !== undefined && mfaCode !== "" ? { mfaCode } : {};
      const holder: { error: unknown } = { error: null };

      setBusy(action.kind);
      setActionError(null);
      setNote(null);
      try {
        switch (action.kind) {
          case "generate": {
            const issued = await generateLicense(scope, { opId: opIdFor(action), ...mfa }, signal);
            if (signal.aborted) return;
            forgetOpId(action);
            setNote("License key generated.");
            setDialog(null);
            // Re-read the face BEFORE holding the key in state: while the cached status still shows
            // "no license", the "drop a key that cannot be shown" effect would discard it at once.
            await refresh();
            if (signal.aborted) return;
            setRevealed({ licenseId: issued.license.license_id, key: issued.licenseKey });
            setPendingFocus("heading");
            break;
          }
          case "regenerate": {
            await regenerateLicense(scope, { opId: opIdFor(action), expectedLicenseId: action.licenseId, ...mfa }, signal);
            if (signal.aborted) return;
            forgetOpId(action);
            setRevealed(null); // the old key is gone; the new one stays masked until Reveal or Copy
            setNote("License key regenerated.");
            setDialog(null);
            await refresh();
            setPendingFocus("regenerate");
            break;
          }
          case "revoke": {
            await revokeLicense(scope, { opId: opIdFor(action), expectedLicenseId: action.licenseId, ...mfa }, signal);
            if (signal.aborted) return;
            forgetOpId(action);
            setRevealed(null);
            setNote("License key revoked.");
            setDialog(null);
            await refresh();
            setPendingFocus("heading");
            break;
          }
          case "reveal": {
            const key = await revealLicense(scope, mfa, signal);
            if (signal.aborted) return;
            setRevealed({ licenseId: action.licenseId, key });
            setNote("License key shown.");
            setDialog(null);
            break;
          }
          case "copy": {
            const getKey = (): Promise<string> =>
              (revealed !== null && revealed.licenseId === action.licenseId
                ? Promise.resolve(revealed.key)
                : revealLicense(scope, mfa, signal)
              ).catch((error: unknown) => {
                holder.error = error;
                throw error;
              });
            try {
              await writeToClipboard(getKey);
            } catch {
              if (signal.aborted) return;
              if (isStepUpRequired(holder.error)) {
                setDialog({ kind: "stepup", action, wrongCode: mfaCode !== undefined });
                return;
              }
              if (holder.error instanceof ApiError) {
                setDialog(null);
                setActionError(licenseErrorMessage(holder.error, "Could not copy the license key."));
                return;
              }
              // The clipboard refused or does not exist: say so and offer Reveal (Requirement 12.8).
              setDialog(null);
              setActionError(COPY_FAILED_TEXT);
              setPendingFocus("reveal");
              return;
            }
            if (signal.aborted) return;
            setNote("License key copied");
            setDialog(null);
            break;
          }
        }
      } catch (error) {
        if (signal.aborted) return;
        if (isStepUpRequired(error)) {
          setDialog({ kind: "stepup", action, wrongCode: mfaCode !== undefined });
          return;
        }
        setDialog(null);
        if (error instanceof ApiError && error.code !== null && DEFINITIVE_CODES.has(error.code)) forgetOpId(action);
        if (error instanceof ApiError && error.code === "rate_limited") {
          setLimitedUntil(Date.now() + (error.retryAfterSeconds ?? 60) * 1000);
        }
        setActionError(licenseErrorMessage(error, "Something went wrong. Please try again."));
        if (error instanceof ApiError && (error.code === "not_active" || error.code === "already_active")) {
          await refresh();
        }
      } finally {
        if (!signal.aborted) setBusy(null);
      }
    },
    [scope, revealed, opIdFor, forgetOpId, refresh],
  );

  const onToggleReveal = (): void => {
    if (face === null) return;
    if (shownKey !== null) {
      setRevealed(null);
      setActionError(null);
      setNote("License key hidden.");
      return;
    }
    void perform({ kind: "reveal", licenseId: face.license_id });
  };

  const guidance = !canManage && data !== undefined ? "Your role cannot generate license keys. Ask an admin." : null;
  const reason = !configured ? `${NOT_CONFIGURED_TEXT}.` : null;

  const title = props.heading ?? `${titleCase(env)} license key`;
  const statusText = statusQuery.isPending ? "Loading license…" : note;
  const errorText = actionError ?? (statusQuery.isError && data === undefined ? licenseErrorMessage(statusQuery.error, "Could not load the license key.") : null);

  const mismatch = scope === "tenant" && isActive ? data?.mismatch ?? null : null;
  const showMismatch = mismatch !== null && (mismatch.client_name || mismatch.key_holder);
  const visibleHistory = (data?.history ?? []).filter((item) => item !== face);

  const writesDisabled = working || writeBlocked;
  const generateDisabled = writesDisabled || !configured;

  return (
    <article
      className={joinClass("oa-license", props.className)}
      aria-labelledby={headingId}
      data-scope={scope}
      data-env={env}
      data-state={state}
      aria-busy={statusQuery.isPending || working ? "true" : undefined}
    >
      <h2 className={joinClass("oa-license__title", props.hideTitle === true ? "sr-only" : undefined)} id={headingId} tabIndex={-1} ref={headingRef}>
        {title}
        {props.heading !== undefined && <span className="sr-only"> ({env})</span>}
      </h2>

      <div className="oa-license__card">
        {/* ---- Face ---------------------------------------------------------------------------- */}
        <div className="oa-license__face" data-status={face?.status ?? "none"} data-placeholder={face === null ? "true" : undefined}>
          <div className="oa-license__face-inner">
            <div className="oa-license__top">
              <span className="oa-license__brand">OATHERA</span>
              <span className="oa-license__badge" data-env={env}>
                {scope === "tenant" ? <ShieldIcon /> : <WrenchIcon />}
                <span>{envLabel}</span>
              </span>
            </div>

            <div className="oa-license__mid">
              <span className="oa-license__chip" aria-hidden="true" />
              {face !== null ? (
                <p className="oa-license__number">{face.card_number}</p>
              ) : (
                <p className="oa-license__number oa-license__number--empty" aria-hidden="true">
                  •••• •••• •••• ••••
                </p>
              )}
            </div>

            {face !== null ? (
              <dl className="oa-license__facts">
                <div className="oa-license__fact">
                  <dt>Client</dt>
                  <dd title={face.client_name}>{face.client_name}</dd>
                </div>
                <div className="oa-license__fact">
                  <dt>Key holder</dt>
                  <dd title={face.holder.name}>{face.holder.name}</dd>
                </div>
                <div className="oa-license__fact">
                  <dt>{face.status === "revoked" ? "Expires (was)" : "Expires"}</dt>
                  <dd>
                    <DateText ms={face.expires_at} />
                  </dd>
                </div>
                <div className="oa-license__fact">
                  <dt>Status</dt>
                  <dd>
                    <StatusPill tone={STATUS_TONE[face.status]}>{STATUS_LABEL[face.status]}</StatusPill>
                  </dd>
                </div>
              </dl>
            ) : (
              <div className="oa-license__empty-face">
                {state === "loading" || state === "error" ? (
                  <span className="oa-license__skeleton" aria-hidden="true" />
                ) : (
                  <p className="oa-license__empty-text">No license key yet</p>
                )}
              </div>
            )}

            <div className="oa-license__foot-row">
              <p className="oa-license__foot">
                {face !== null ? (
                  <>
                    <span>{face.alg}</span>
                    <span aria-hidden="true"> · </span>
                    <span>kid {shortKid(face.issuer_kid)}</span>
                  </>
                ) : (
                  <span aria-hidden="true">ML-DSA-65 · kid ————</span>
                )}
              </p>
              {/* The polite status message ("License key shown.", "...hidden.") sits on the same row as
                  the algorithm and key id. It stays mounted so announcements fire. */}
              <div className="oa-license__live oa-license__live--foot" role="status" aria-live="polite" aria-atomic="true">
                {statusText !== null && (
                  <p className="oa-account__feedback" data-kind={statusQuery.isPending ? "info" : "ok"}>
                    {statusText}
                  </p>
                )}
              </div>
            </div>

            {face !== null && face.status !== "active" && (
              <span className="oa-license__stamp" aria-hidden="true">
                {STATUS_LABEL[face.status].toUpperCase()}
              </span>
            )}
          </div>
        </div>

        {/* ---- Key strip ----------------------------------------------------------------------- */}
        <div className="oa-license__strip">
          {face !== null && (
            <>
              {shownKey !== null ? (
                <div id={keyRegionId} className="oa-license__key oa-license__key--revealed" role="region" aria-label="License key" tabIndex={0}>
                  {shownKey}
                </div>
              ) : (
                <div id={keyRegionId} className="oa-license__key" data-state={face.status === "active" ? "masked" : "unavailable"}>
                  <span aria-hidden="true">{HIDDEN_KEY_TEXT}</span>
                  <span className="sr-only">License key hidden</span>
                </div>
              )}
            </>
          )}

          <div className="oa-license__actions">
            {canManage && isActive && face !== null && (
              <button
                type="button"
                className="oa-account__btn oa-account__btn--ghost"
                aria-label={`Copy ${env} license key`}
                disabled={working}
                onClick={() => void perform({ kind: "copy", licenseId: face.license_id })}
              >
                {busy === "copy" ? "Copying…" : "Copy"}
              </button>
            )}

            {canManage && (isExpired || isHistorical) && (
              /* Copy stays on the card, disabled, so the state reads visibly (12.11). */
              <button type="button" className="oa-account__btn oa-account__btn--ghost" aria-label={`Copy ${env} license key`} disabled>
                Copy
              </button>
            )}

            {canManage && (isActive || isExpired) && current !== null && (
              <>
                <button
                  ref={regenerateButtonRef}
                  type="button"
                  className="oa-account__btn"
                  aria-label={`Regenerate ${env} license key`}
                  aria-describedby={reason !== null ? reasonId : undefined}
                  disabled={writesDisabled || !configured}
                  onClick={() => {
                    setNote(null);
                    setActionError(null);
                    setDialog({ kind: "confirm", intent: "regenerate" });
                  }}
                >
                  Regenerate
                </button>
                <button
                  type="button"
                  className="oa-account__btn oa-account__btn--danger"
                  aria-label={`Revoke ${env} license key`}
                  disabled={writesDisabled}
                  onClick={() => {
                    setNote(null);
                    setActionError(null);
                    setDialog({ kind: "confirm", intent: "revoke" });
                  }}
                >
                  Revoke
                </button>
              </>
            )}

            {canManage && (face === null || isHistorical) && state !== "loading" && state !== "error" && (
              <button
                type="button"
                className="oa-account__btn"
                aria-label={`Generate ${env} license key`}
                aria-describedby={reason !== null ? reasonId : undefined}
                disabled={generateDisabled}
                onClick={() => void perform({ kind: "generate" })}
              >
                {busy === "generate" ? "Generating…" : "Generate"}
              </button>
            )}

            {state === "error" && (
              <button type="button" className="oa-account__btn" onClick={() => void statusQuery.refetch()} disabled={statusQuery.isFetching}>
                {statusQuery.isFetching ? "Retrying…" : "Retry"}
              </button>
            )}

            {/* The Reveal / Hide toggle is an icon button at the right end of the row; the other
                buttons stay on the left. */}
            {canManage && isActive && face !== null && (
              <button
                ref={revealButtonRef}
                type="button"
                className="oa-account__btn oa-account__btn--ghost oa-license__icon-btn"
                aria-pressed={shownKey !== null}
                aria-controls={keyRegionId}
                aria-label={`${shownKey !== null ? "Hide" : "Reveal"} ${env} license key`}
                title={`${shownKey !== null ? "Hide" : "Reveal"} license key`}
                disabled={working}
                onClick={onToggleReveal}
              >
                <EyeIcon off={shownKey !== null} />
              </button>
            )}
            {canManage && (isExpired || isHistorical) && (
              <button
                ref={revealButtonRef}
                type="button"
                className="oa-account__btn oa-account__btn--ghost oa-license__icon-btn"
                aria-label={`Reveal ${env} license key`}
                title="Reveal license key"
                aria-pressed={false}
                aria-controls={keyRegionId}
                disabled
              >
                <EyeIcon off={false} />
              </button>
            )}
          </div>

          {reason !== null && canManage && (
            <p className="oa-license__reason" id={reasonId}>
              {reason}
            </p>
          )}
          {guidance !== null && (
            <p className="oa-license__reason">{guidance}</p>
          )}
          {isExpired && (
            <p className="oa-license__reason">This key has expired. Regenerate it to get a new one.</p>
          )}
          {isHistorical && face.status === "revoked" && (
            <p className="oa-license__reason">This key was revoked. Generate a new one if you still need it.</p>
          )}
          {writeBlocked && (
            <p className="oa-license__reason">License changes are paused for a short while. Try again shortly.</p>
          )}
        </div>
      </div>

      {showMismatch && data !== undefined && face !== null && (
        <div className="oa-license__notice" data-kind="mismatch">
          <p className="oa-license__notice-title">The team name or key holder has changed since this key was issued.</p>
          <ul className="oa-license__notice-list">
            {mismatch.client_name && (
              <li>
                Team name: <strong>{face.client_name}</strong> on the key, <strong>{data.current?.client_name ?? "unknown"}</strong> now.
              </li>
            )}
            {mismatch.key_holder && (
              <li>
                Key holder: <strong>{face.holder.name}</strong> on the key, <strong>{data.current?.holder_name ?? "none"}</strong> now.
              </li>
            )}
          </ul>
          <p className="oa-license__notice-body">Regenerate the key to match. The issued key is never changed automatically. {OLD_KEY_WARNING}</p>
        </div>
      )}

      {visibleHistory.length > 0 && (
        <button
          type="button"
          className="oa-license__history-link"
          aria-haspopup="dialog"
          onClick={() => setHistoryOpen(true)}
        >
          History ({visibleHistory.length})
        </button>
      )}

      {/* The history opens in the right-hand flyout, like the other detail views. It holds metadata only
          (status, dates, key id), never a key. It is mounted only while open so the shared dialog focus hook
          (focus in, Tab trap, Escape, focus back to the link) runs on open. */}
      {historyOpen && visibleHistory.length > 0 && (
      <Drawer open onClose={() => setHistoryOpen(false)} title={`${titleCase(env)} license key history`}>
        <p className="oa-license-history__intro">Earlier keys for this {scope === "tenant" ? "team" : "account"}, newest first.</p>
        <ul className="oa-license-history__list">
          {visibleHistory.map((item) => (
            <li key={item.license_id} className="oa-license-history__item">
              <StatusPill tone={STATUS_TONE[item.status]}>{STATUS_LABEL[item.status]}</StatusPill>
              <span className="oa-license-history__dates">
                Issued <DateText ms={item.issued_at} />
                {", "}
                {item.status === "revoked" && item.revoked_at !== null ? "revoked" : "expires"}{" "}
                <DateText ms={item.status === "revoked" && item.revoked_at !== null ? item.revoked_at : item.expires_at} />
              </span>
              <span className="oa-license-history__kid">kid {shortKid(item.issuer_kid)}</span>
            </li>
          ))}
        </ul>
      </Drawer>
      )}

      {/* The alert region stays mounted so announcements fire; the status region is on the face. */}
      <div className="oa-license__live" role="alert">
        {errorText !== null && (
          <p className="oa-account__feedback" data-kind="error">
            {errorText}
          </p>
        )}
      </div>

      {/* Mounted only while open so the shared dialog focus hook runs on open and restores focus
          to the invoking button on close. */}
      {dialog !== null && dialog.kind === "confirm" && current !== null && (
        <Modal
          key="confirm"
          open
          role="alertdialog"
          onClose={() => setDialog(null)}
          title={dialog.intent === "regenerate" ? "Regenerate license key?" : "Revoke license key?"}
          description={
            dialog.intent === "regenerate"
              ? `This revokes the current ${env} key and issues a new one. ${OLD_KEY_WARNING}`
              : `This revokes the current ${env} key. ${OLD_KEY_WARNING} A revoked key cannot be restored.`
          }
          footer={
            <>
              <button type="button" className="oa-account__btn oa-account__btn--ghost" disabled={working} onClick={() => setDialog(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="oa-account__btn oa-account__btn--danger"
                disabled={working}
                onClick={() =>
                  void perform(
                    dialog.intent === "regenerate"
                      ? { kind: "regenerate", licenseId: current.license_id }
                      : { kind: "revoke", licenseId: current.license_id },
                  )
                }
              >
                {working ? "Working…" : dialog.intent === "regenerate" ? "Regenerate key" : "Revoke key"}
              </button>
            </>
          }
        >
          <p className="oa-account__card-note">
            {dialog.intent === "regenerate"
              ? "Update every device that uses this key once the new one is issued."
              : "Devices that present this key will be refused until they are given a new one."}
          </p>
        </Modal>
      )}

      {dialog !== null && dialog.kind === "stepup" && (
        <StepUpDialog
          key="stepup"
          wrongCode={dialog.wrongCode}
          busy={working}
          onCancel={() => setDialog(null)}
          onSubmit={(code) => void perform(dialog.action, code)}
        />
      )}
    </article>
  );
}

/**
 * Write the key to the clipboard. `getKey` is called synchronously inside the click gesture, and its
 * promise is handed to `ClipboardItem` so the browser keeps the user-gesture permission across the
 * network round trip. The key is never rendered on this path. Rejects with
 * {@link ClipboardUnavailableError} when there is no clipboard, or with the browser's refusal.
 */
async function writeToClipboard(getKey: () => Promise<string>): Promise<void> {
  const clipboard = typeof navigator === "undefined" ? undefined : navigator.clipboard;
  if (clipboard === undefined) throw new ClipboardUnavailableError("clipboard unavailable");

  if (typeof ClipboardItem !== "undefined" && typeof clipboard.write === "function") {
    const blob = getKey().then((key) => new Blob([key], { type: "text/plain" }));
    blob.catch(() => undefined); // the rejection is surfaced through clipboard.write below
    await clipboard.write([new ClipboardItem({ "text/plain": blob })]);
    return;
  }
  if (typeof clipboard.writeText === "function") {
    await clipboard.writeText(await getKey());
    return;
  }
  throw new ClipboardUnavailableError("clipboard unavailable");
}
